import moment, { Moment } from 'moment';
import { saveAs } from 'file-saver';
import { ApplicantAdditionalDetailsType } from '../components/AdditionalDetailsType';
import { CategoryAnswerType } from '../pages/applicant/NewApplicationWizard/CategoryAnswerType';
import {
	ApplicationStatus,
	CTDCategories,
	DEFAULT_DATE,
	DEFAULT_PAGE_SIZE,
	EvaluationCategoryID,
	FUNCTIONARY_TYPE_LABELS,
	PersonRole,
	PROJECT_CODE_FOR_MAINTENANCE,
	ReferenceCategory,
	Relationship,
	ReviewAction,
} from './Constants';
import { ChildrenMap, GroupMap, GroupNode, MeProfile } from './MySrcm';
import {
	Answer,
	ApplicantAdditionalDetails,
	ApplicantInfo,
	ApplicantReference,
	Application,
	ApplicationsFilter,
	CategoryReviewFeedback,
	Center,
	Feedback,
	FunctionaryFeedbackRequest,
	Pagination,
	PagingConfig,
	Preceptor,
	PreworkResponseData,
	User,
	UserRole,
} from './VolunteerApplicationServiceTypes';
import isEmpty from 'lodash/isEmpty';
import { PreWorkStepType } from '../pages/applicant/PreWorkWizard/PreWorkStepType';
import { ShowTotalForTablePagination } from '../components/ShowTotalForTablePagination';
import { TablePaginationConfig } from 'antd';
import { Exam, StringToStringMap } from './ExamModuleTypes';
import { TrainingCourseDetailsType } from '../components/TrainingCourseDetailsType';
import { PreceptorContactDetailsType } from '../components/PreceptorContactDetailsType';
import { FamilyMemberDetailsType } from '../components/FamilyMemberDetailsType';
import { OtherReferenceDetailsType } from '../components/OtherReferenceDetailsType';
import { CentralTeamDashboardFilter } from './ScreeningServiceTypes';
import { AxiosError } from 'axios';
import {
	PreworkCompletionStatus,
	PreworkReviewAction,
	PreworkStatus,
} from './PreWorkServiceTypes';
import dayjs, { extend } from 'dayjs';
import { RolePrivilege } from './RoleBasedTypes';
import customParseFormat from 'dayjs/plugin/customParseFormat';

export const SELECTED_ROLES = [
	UserRole.SNP_ADMIN,
	UserRole.SCREENING_ADMIN,
	UserRole.APPROVER,
	UserRole.EXAM_ADMIN,
];

export const ALL_ROLES = [
	UserRole.SNP_ADMIN,
	UserRole.SCREENING_ADMIN,
	UserRole.APPROVER,
	UserRole.CARE_TEAM,
	UserRole.EXAM_ADMIN,
	UserRole.NPS_ADMIN,
	UserRole.PREWORK_ADMIN,
	UserRole.PREWORK_MENTOR,
	UserRole.ZC,
	UserRole.PROPOSER,
];

export const transformSRCMGenderToGRPCGender = (srcmGender?: string) => {
	if (srcmGender === 'M') {
		return 'MALE';
	}

	if (srcmGender === 'F') {
		return 'FEMALE';
	}

	return 'NOT_PROVIDED';
};

export const transformMySrcmProfileToGRPCUser = (
	mySrcmProfile: MeProfile
): User => {
	return {
		mySrcmPartnerId: mySrcmProfile.id,
		photoUrl: mySrcmProfile.photo_url,
		firstName: mySrcmProfile.first_name,
		middleName: mySrcmProfile.middle_name,
		lastName: mySrcmProfile.last_name,
		addressLine1: mySrcmProfile.street,
		addressLine2: mySrcmProfile.street2,
		addressLine3: mySrcmProfile.street3,
		addressLine4: mySrcmProfile.street4,
		city: mySrcmProfile.city,
		mobile: mySrcmProfile.mobile,
		email: mySrcmProfile.email,
		pincode: mySrcmProfile.postal_code,
		firebaseId: mySrcmProfile.user_firebase_uid,
		srcmId: mySrcmProfile.ref,
		gender: transformSRCMGenderToGRPCGender(mySrcmProfile.gender),
		age: mySrcmProfile.age,
		cityId: mySrcmProfile?.city_id?.id,
		stateId: mySrcmProfile?.state?.id,
		state: mySrcmProfile?.state?.name,
		countryId: mySrcmProfile?.country?.id,
		country: mySrcmProfile?.country?.name,
	};
};

export const centerNameToDisplay = (center: Center) => {
	return `${center.name} - ${center.zone}`;
};

export const transformGRPCApplicationToAdditionalDetails = (
	application: Application
): ApplicantAdditionalDetailsType => {
	let dateOfBirth = undefined;
	let doj = undefined;

	if (application?.applicant?.dateOfBirth) {
		dateOfBirth = moment(application?.applicant?.dateOfBirth, 'DD-MMM-YYYY');
	}

	if (application?.applicant?.dateOfJoining) {
		doj = moment(application?.applicant?.dateOfJoining, 'DD-MMM-YYYY');
	}

	const center = {
		value: JSON.stringify(application.center),
		label: centerNameToDisplay(application.center),
	};

	return {
		center,
		doj,
		dateOfBirth,
		educationQualification: application?.applicant?.educationQualification,
		profession: application?.applicant?.profession,
		professionDetails: application?.applicant?.professionDetails,
		addressLine1: application?.applicant?.addressLine1,
		addressLine2: application?.applicant?.addressLine2,
		addressLine3: application?.applicant?.addressLine3,
		city: application?.applicant?.city,
		pincode: application?.applicant?.pincode,
	};
};

export const transformGRPCApplicationToTrainingCourseDetails = (
	application: Application
): TrainingCourseDetailsType => {
	return {
		preferredTrainingLanguage: application?.preferredLanguageOfTraining,
		availabilityForTakingTheCourse: application?.availabilityForTrainingCourse,
	};
};

export const transformGRPCApplicationToPreceptorContactDetails = (
	application: Application
): PreceptorContactDetailsType => {
	return {
		preceptorName: application?.preceptorReference?.name,
		preceptorMobile: application?.preceptorReference?.mobile,
		preceptorEmail: application?.preceptorReference?.email,
		preceptorSrcmId: application?.preceptorReference?.srcmid,
		preceptorMySrcmPartnerId: application?.preceptorReference?.mySrcmPartnerId,
	};
};

export const transformGRPCApplicationToFamilyMemberDetails = (
	application: Application
): FamilyMemberDetailsType => {
	const relationship = () => {
		if (
			application?.familyMemberReference?.relationship !==
				Relationship.SPOUSE &&
			application?.familyMemberReference?.relationship !== Relationship.PARENT
		) {
			return Relationship.OTHER;
		} else {
			return application?.familyMemberReference?.relationship;
		}
	};

	return {
		familyMemberName: application?.familyMemberReference?.name,
		familyMemberMobile: application?.familyMemberReference?.mobile,
		familyMemberRelationship: relationship(),
		familyMemberRelationshipOtherThanSpouseOrParent:
			application?.familyMemberReference?.relationship,
		familyMemberIsAbhyasi: application?.familyMemberReference?.isAbhyasi,
		familyMemberIsPreceptor: application?.familyMemberReference?.isPreceptor,
		familyMemberRole: application?.familyMemberReference?.isAbhyasi
			? PersonRole.AN_ABHYASI
			: application?.familyMemberReference?.isPreceptor
			? PersonRole.A_PRECEPTOR
			: PersonRole.NOT_YET_AN_ABHYASI,
	};
};

export const transformGRPCApplicationToOtherReferenceDetails = (
	application: Application
): OtherReferenceDetailsType => {
	return {
		otherReferenceProvided: application?.otherReferenceProvided,
		otherReferenceName: application?.otherReference?.name,
		otherReferenceMobile: application?.otherReference?.mobile,
		otherReferenceIsAbhyasi: application?.otherReference?.isAbhyasi,
		otherReferenceIsPreceptor: application?.otherReference?.isPreceptor,
		otherReferenceRole: application?.otherReference?.isAbhyasi
			? PersonRole.AN_ABHYASI
			: application?.otherReference?.isPreceptor
			? PersonRole.A_PRECEPTOR
			: PersonRole.NOT_YET_AN_ABHYASI,
	};
};

export const transformAdditionalDetailsToGRPC = (
	additionalDetails: ApplicantAdditionalDetailsType,
	trainingCourseDetails: TrainingCourseDetailsType,
	preceptorContactDetails: PreceptorContactDetailsType,
	familyMemberContactDetails: FamilyMemberDetailsType,
	otherReferenceContactDetails: OtherReferenceDetailsType,
	originalPreceptorReference?: ApplicantReference
): ApplicantAdditionalDetails => {
	// eslint-disable-next-line
	const center = parseInt(additionalDetails.center.id);

	return {
		center,
		monthOfJoiningInMMMYYYY: additionalDetails.doj?.format('DD-MMM-YYYY'),
		educationQualification: additionalDetails.educationQualification,
		profession: additionalDetails.profession,
		professionDetails: additionalDetails.professionDetails,
		addressLine1: additionalDetails.addressLine1,
		addressLine2: additionalDetails.addressLine2,
		addressLine3: additionalDetails.addressLine3,
		city: additionalDetails.city,
		pincode: additionalDetails.pincode,
		dateOfBirth: additionalDetails.dateOfBirth?.format('DD-MMM-YYYY'),
		preceptorReference: {
			name: preceptorContactDetails.preceptorName,
			category: ReferenceCategory.PRECEPTOR,
			isAbhyasi: true,
			isPreceptor: true,
			mobile: preceptorContactDetails.preceptorMobile,
			email:
				originalPreceptorReference?.email ||
				preceptorContactDetails?.preceptorEmail,
			srcmid:
				originalPreceptorReference?.srcmid ||
				preceptorContactDetails?.preceptorSrcmId,
			mySrcmPartnerId: preceptorContactDetails?.preceptorMySrcmPartnerId,
		},
		familyMemberReference: {
			name: familyMemberContactDetails.familyMemberName,
			category: ReferenceCategory.FAMILY_MEMBER,
			isAbhyasi:
				familyMemberContactDetails.familyMemberRole === PersonRole.AN_ABHYASI,
			isPreceptor:
				familyMemberContactDetails.familyMemberRole === PersonRole.A_PRECEPTOR,
			mobile: familyMemberContactDetails.familyMemberMobile,
			relationship:
				familyMemberContactDetails.familyMemberRelationship ===
				Relationship.OTHER
					? familyMemberContactDetails.familyMemberRelationshipOtherThanSpouseOrParent
					: familyMemberContactDetails.familyMemberRelationship,
		},
		otherReference: {
			name: otherReferenceContactDetails.otherReferenceName,
			category: ReferenceCategory.OTHER_REFERENCE,
			isAbhyasi:
				otherReferenceContactDetails.otherReferenceRole ===
				PersonRole.AN_ABHYASI,
			isPreceptor:
				otherReferenceContactDetails.otherReferenceRole ===
				PersonRole.A_PRECEPTOR,
			mobile: otherReferenceContactDetails.otherReferenceMobile,
		},
		otherReferenceProvided:
			otherReferenceContactDetails.otherReferenceProvided ?? false,
		preferredLanguageOfTraining:
			trainingCourseDetails.preferredTrainingLanguage,
		availabilityForTrainingCourse:
			trainingCourseDetails.availabilityForTakingTheCourse,
	};
};

export const transformAnswerToGRPC = (
	categoryId: number,
	categoryAnswers: CategoryAnswerType
): Array<Answer> => {
	const grpcAnswers: Array<Answer> = [];

	for (const key in categoryAnswers) {
		const { selectedOption, comments } = categoryAnswers[key];
		grpcAnswers.push({
			categoryId,
			questionId: parseInt(key),
			answerOptionId: selectedOption,
			additionalComment: comments,
		});
	}

	return grpcAnswers;
};

export const isProposerPartCompleted = (statusValue?: string) => {
	if (!statusValue) {
		return false;
	}

	return [
		ApplicationStatus.FUNCTIONARY_SUBMITTED,
		ApplicationStatus.SYS_ANOTHER_FUNCTIONARY_ASSIGNED,
		ApplicationStatus.APPROVED,
		ApplicationStatus.REJECTED,
	].includes(statusValue);
};

export const canSubmitApproverFeedback = (application?: Application) => {
	if (!application?.isApprover) {
		return false;
	}

	return [
		ApplicationStatus.FUNCTIONARY_SUBMITTED,
		ApplicationStatus.SYS_ANOTHER_FUNCTIONARY_ASSIGNED,
	].includes(application?.applicationStatus);
};

export const isApproverPartCompleted = (statusValue?: string) => {
	if (!statusValue) {
		return false;
	}

	return [ApplicationStatus.APPROVED, ApplicationStatus.REJECTED].includes(
		statusValue
	);
};

export const isProposerSkippedToEnd = (proposerRecommendation?: Feedback) => {
	const isSkippedToEnd =
		proposerRecommendation?.review === ReviewAction.REJECT &&
		proposerRecommendation?.predefinedReasons?.some(
			(reason) => reason.category === 'SKIP'
		);

	return isSkippedToEnd;
};

export const addMandatoryQuestionsToFunctionaryFeedback = (
	functionaryFeedback: FunctionaryFeedbackRequest,
	language: string
): FunctionaryFeedbackRequest => {
	const { categoryReviewFeedback } = functionaryFeedback;
	const answers: Array<Answer> = [];

	const availabilityAfterProgramFeedback =
		categoryReviewFeedback.availabilityAfterProgram;
	if (
		availabilityAfterProgramFeedback &&
		!isEmpty(availabilityAfterProgramFeedback.mandatoryQuestionsWithAnswers)
	) {
		answers.push(
			...transformAnswerToGRPC(
				EvaluationCategoryID.AVAILABILITY_AFTER_PROGRAM,
				availabilityAfterProgramFeedback.mandatoryQuestionsWithAnswers
			)
		);
	}

	const characterAndHabitsFeedback = categoryReviewFeedback.characterAndHabits;
	if (
		characterAndHabitsFeedback &&
		!isEmpty(characterAndHabitsFeedback.mandatoryQuestionsWithAnswers)
	) {
		answers.push(
			...transformAnswerToGRPC(
				EvaluationCategoryID.CHARACTER_AND_HABITS,
				characterAndHabitsFeedback.mandatoryQuestionsWithAnswers
			)
		);
	}

	const inspirationAndWillingnessFeedback =
		categoryReviewFeedback.inspirationAndWillingness;
	if (
		inspirationAndWillingnessFeedback &&
		!isEmpty(inspirationAndWillingnessFeedback?.mandatoryQuestionsWithAnswers)
	) {
		answers.push(
			...transformAnswerToGRPC(
				EvaluationCategoryID.INSPIRATION_AND_WILLINGNESS,
				inspirationAndWillingnessFeedback.mandatoryQuestionsWithAnswers
			)
		);
	}

	const practiceFeedback = categoryReviewFeedback.practice;
	if (
		practiceFeedback &&
		!isEmpty(practiceFeedback?.mandatoryQuestionsWithAnswers)
	) {
		answers.push(
			...transformAnswerToGRPC(
				EvaluationCategoryID.PRACTICE,
				practiceFeedback.mandatoryQuestionsWithAnswers
			)
		);
	}

	const supportFeedback = categoryReviewFeedback.support;
	if (
		supportFeedback &&
		!isEmpty(supportFeedback?.mandatoryQuestionsWithAnswers)
	) {
		answers.push(
			...transformAnswerToGRPC(
				EvaluationCategoryID.SUPPORT,
				supportFeedback.mandatoryQuestionsWithAnswers
			)
		);
	}

	const understandingOfHeartfulnessFeedback =
		categoryReviewFeedback.understandingOfHeartfulness;
	if (
		understandingOfHeartfulnessFeedback &&
		!isEmpty(understandingOfHeartfulnessFeedback?.mandatoryQuestionsWithAnswers)
	) {
		answers.push(
			...transformAnswerToGRPC(
				EvaluationCategoryID.UNDERSTANDING_OF_HEARTFULNESS,
				understandingOfHeartfulnessFeedback.mandatoryQuestionsWithAnswers
			)
		);
	}

	const dietaryHabitsAndHealthFeedback =
		categoryReviewFeedback.dietaryHabitsAndHealth;
	if (
		dietaryHabitsAndHealthFeedback &&
		!isEmpty(dietaryHabitsAndHealthFeedback?.mandatoryQuestionsWithAnswers)
	) {
		answers.push(
			...transformAnswerToGRPC(
				EvaluationCategoryID.DIETARY_HABITS_AND_HEALTH,
				dietaryHabitsAndHealthFeedback.mandatoryQuestionsWithAnswers
			)
		);
	}

	const eligibilityCriteriaFeedback =
		categoryReviewFeedback.eligibilityCriteria;
	if (
		eligibilityCriteriaFeedback &&
		!isEmpty(eligibilityCriteriaFeedback?.mandatoryQuestionsWithAnswers)
	) {
		answers.push(
			...transformAnswerToGRPC(
				EvaluationCategoryID.BASIC_ELIGIBILITY,
				eligibilityCriteriaFeedback.mandatoryQuestionsWithAnswers
			)
		);
	}

	categoryReviewFeedback.answersToMandatoryQuestions = {
		language,
		questionsAndAnswers: answers,
	};

	functionaryFeedback.categoryReviewFeedback = categoryReviewFeedback;
	return functionaryFeedback;
};

export const transformToAnswersForMandatoryQuestionsFromGRPC = (
	interviewFeedback: CategoryReviewFeedback | undefined
): CategoryReviewFeedback | undefined => {
	const questionsAndAnswers =
		interviewFeedback?.answersToMandatoryQuestions?.questionsAndAnswers;

	if (!questionsAndAnswers?.length) return interviewFeedback;

	const inspirationAndWillingnessCategoryAnswerTypeObject: CategoryAnswerType =
		{};
	const practiceCategoryAnswerTypeObject: CategoryAnswerType = {};
	const availabilityAfterProgramCategoryAnswerTypeObject: CategoryAnswerType =
		{};
	const characterAndHabitsCategoryAnswerTypeObject: CategoryAnswerType = {};
	const supportCategoryAnswerTypeObject: CategoryAnswerType = {};
	const understandingOfHeartfulnessCategoryAnswerTypeObject: CategoryAnswerType =
		{};
	const dietaryHabitsAndHealthCategoryAnswerTypeObject: CategoryAnswerType = {};
	const eligibilityCriteriaCategoryAnswerTypeObject: CategoryAnswerType = {};

	questionsAndAnswers.forEach((question) => {
		if (
			question.categoryId === EvaluationCategoryID.INSPIRATION_AND_WILLINGNESS
		) {
			inspirationAndWillingnessCategoryAnswerTypeObject[question.questionId] = {
				selectedOption: question.answerOptionId,
				comments: question.additionalComment,
			};
		} else if (question.categoryId === EvaluationCategoryID.PRACTICE) {
			practiceCategoryAnswerTypeObject[question.questionId] = {
				selectedOption: question.answerOptionId,
				comments: question.additionalComment,
			};
		} else if (
			question.categoryId === EvaluationCategoryID.AVAILABILITY_AFTER_PROGRAM
		) {
			availabilityAfterProgramCategoryAnswerTypeObject[question.questionId] = {
				selectedOption: question.answerOptionId,
				comments: question.additionalComment,
			};
		} else if (
			question.categoryId === EvaluationCategoryID.CHARACTER_AND_HABITS
		) {
			characterAndHabitsCategoryAnswerTypeObject[question.questionId] = {
				selectedOption: question.answerOptionId,
				comments: question.additionalComment,
			};
		} else if (question.categoryId === EvaluationCategoryID.SUPPORT) {
			supportCategoryAnswerTypeObject[question.questionId] = {
				selectedOption: question.answerOptionId,
				comments: question.additionalComment,
			};
		} else if (
			question.categoryId === EvaluationCategoryID.UNDERSTANDING_OF_HEARTFULNESS
		) {
			understandingOfHeartfulnessCategoryAnswerTypeObject[question.questionId] =
				{
					selectedOption: question.answerOptionId,
					comments: question.additionalComment,
				};
		} else if (
			question.categoryId === EvaluationCategoryID.DIETARY_HABITS_AND_HEALTH
		) {
			dietaryHabitsAndHealthCategoryAnswerTypeObject[question.questionId] = {
				selectedOption: question.answerOptionId,
				comments: question.additionalComment,
			};
		} else if (question.categoryId === EvaluationCategoryID.BASIC_ELIGIBILITY) {
			eligibilityCriteriaCategoryAnswerTypeObject[question.questionId] = {
				selectedOption: question.answerOptionId,
				comments: question.additionalComment,
			};
		}
	});

	if (interviewFeedback) {
		if (interviewFeedback.inspirationAndWillingness) {
			interviewFeedback.inspirationAndWillingness.mandatoryQuestionsWithAnswers =
				inspirationAndWillingnessCategoryAnswerTypeObject;
		}

		if (interviewFeedback.practice) {
			interviewFeedback.practice.mandatoryQuestionsWithAnswers =
				practiceCategoryAnswerTypeObject;
		}

		if (interviewFeedback.availabilityAfterProgram) {
			interviewFeedback.availabilityAfterProgram.mandatoryQuestionsWithAnswers =
				availabilityAfterProgramCategoryAnswerTypeObject;
		}

		if (interviewFeedback.characterAndHabits) {
			interviewFeedback.characterAndHabits.mandatoryQuestionsWithAnswers =
				characterAndHabitsCategoryAnswerTypeObject;
		}

		if (interviewFeedback.support) {
			interviewFeedback.support.mandatoryQuestionsWithAnswers =
				supportCategoryAnswerTypeObject;
		}

		if (interviewFeedback.understandingOfHeartfulness) {
			interviewFeedback.understandingOfHeartfulness.mandatoryQuestionsWithAnswers =
				understandingOfHeartfulnessCategoryAnswerTypeObject;
		}

		if (interviewFeedback.dietaryHabitsAndHealth) {
			interviewFeedback.dietaryHabitsAndHealth.mandatoryQuestionsWithAnswers =
				dietaryHabitsAndHealthCategoryAnswerTypeObject;
		}

		if (interviewFeedback.eligibilityCriteria) {
			interviewFeedback.eligibilityCriteria.mandatoryQuestionsWithAnswers =
				eligibilityCriteriaCategoryAnswerTypeObject;
		}
	}

	return interviewFeedback;
};

const generatePreworkAnswers = (
	questionsAndAnswers: Answer[]
): PreWorkStepType => {
	const preworkAnswers: PreWorkStepType = {
		foundationOfServingIsIntegrity: false,
	};

	questionsAndAnswers.forEach((question) => {
		if (question.categoryId === EvaluationCategoryID.PRE_WORK) {
			preworkAnswers[question.questionId] = {
				selectedOption: question.answerOptionId,
				comments: question.additionalComment,
			};
		}
	});

	return preworkAnswers;
};

export const transformGRPCToPreworkAnswers = (
	preworkData: PreworkResponseData
): PreWorkStepType => {
	const questionsAndAnswers = preworkData.userAnswers.questionsAndAnswers;

	const preworkAnswers = generatePreworkAnswers(questionsAndAnswers);

	return preworkAnswers;
};

export const transformGRPCToPreworkMentorAnswers = (
	preworkData: PreworkResponseData
): PreWorkStepType => {
	const questionsAndAnswers = preworkData.mentorAnswers.questionsAndAnswers;

	const preworkMentorAnswers = generatePreworkAnswers(questionsAndAnswers);

	return preworkMentorAnswers;
};

const base64Marker = ';base64,';
export const downloadFile = (
	content: string,
	name = 'download',
	type = 'url',
	mimeType = ''
) => {
	if (!content) return;

	if (type === 'url' || type === 'blob') {
		saveAs(content, name);
	} else if (type === 'StringByteArray') {
		let base64String = content;
		if (base64String.includes(base64Marker)) {
			const base64Index =
				base64String.indexOf(base64Marker) + base64Marker.length;
			base64String = base64String.substring(base64Index);
		}
		const binaryString = window.atob(base64String);
		const byteArray = Uint8Array.from(binaryString, (char) =>
			char.charCodeAt(0)
		);
		const file = new Blob([byteArray], { type: mimeType });
		saveAs(file, name);
	}
};

export const tablePaginationConfiguration = (
	paging: PagingConfig,
	pagination: Pagination,
	onPageChange: (pageNumber: number, pageSize: number) => void
) => {
	const paginationConfig: TablePaginationConfig = {
		current: paging.pageNo ?? 1,
		pageSize: paging.pageSize ?? DEFAULT_PAGE_SIZE,
		total: pagination.totalNoOfRecords,
		showTotal: ShowTotalForTablePagination,
		onChange: onPageChange,
		showSizeChanger: true,
	};
	return paginationConfig;
};

export const convertDateToUTCString = (
	date: Moment,
	isEndDate = false
): string => {
	if (isEndDate) {
		return date.endOf('day').format('YYYY-MM-DDTHH:mm:ss') + 'Z';
	}
	return date.startOf('day').format('YYYY-MM-DDTHH:mm:ss') + 'Z';
};

export const isWebsiteUnderMaintenance = async () => {
	const projectCode = PROJECT_CODE_FOR_MAINTENANCE;
	const projectsURL = `${
		process.env.REACT_APP_API_STATICDATA_URL ||
		'https://static-file-staging.web.app'
	}/conf/hfn-maintenance-web/projects.json`;
	type MaintenanceConfigType = {
		project_code: string;
		in_maintenance: boolean;
	};
	let project: MaintenanceConfigType | undefined = {
		project_code: '',
		in_maintenance: false,
	};

	try {
		const response = await fetch(projectsURL);
		const projectsData = (await response.json()) as {
			PROJECTS: MaintenanceConfigType[];
		};
		project = projectsData.PROJECTS.find(
			(p: MaintenanceConfigType) => p.project_code === projectCode
		);
	} catch (e) {
		return false;
	}
	return !!(project && project.in_maintenance);
};

export const getAppMode = (mode: string | undefined) => {
	let appMode;
	switch (mode?.toLowerCase()) {
		case 'production':
			appMode = 'PROD';
			break;
		case 'staging':
			appMode = 'STAGING';
			break;
		case 'development':
			appMode = 'DEV';
			break;
		case 'uat':
			appMode = 'UAT';
			break;
		case 'unknown':
			appMode = 'UNKNOWN';
			break;
		default:
			appMode = 'PROD';
			break;
	}
	return appMode;
};

export const filterActiveCentersFromExam = (exam: Exam) => {
	const activeCenters = exam.examinationCenterMappings.filter(
		(examCenter) => examCenter.examinationCenter.status === 'ACTIVE'
	);
	return activeCenters.map((examCenter) => ({
		value: examCenter.examinationCenter.id,
		label: examCenter.examinationCenter.centerName,
	}));
};

export const preprocessContent = (
	input: string | undefined,
	placeholderMap: StringToStringMap
) => {
	if (input) {
		let output = input;

		for (const [placeholder, original] of Object.entries(placeholderMap)) {
			output = output.replaceAll(original, placeholder);
		}

		return output;
	}
};

export const restoreContent = (
	input: string,
	placeholderMap: StringToStringMap
) => {
	let output = input;

	for (const [placeholder, original] of Object.entries(placeholderMap)) {
		output = output.replaceAll(placeholder, original);
	}

	return output;
};

export const checkImage = async (url: string) => {
	if (!url) {
		return false;
	}

	try {
		const response: Response = await fetch(url, { method: 'HEAD' });
		return response.status === 404 ? false : true;
	} catch (error) {
		return false;
	}
};

export const convertStringToInt = (
	stringValue: string | number | undefined
) => {
	if (stringValue) {
		const intValue =
			typeof stringValue === 'string' ? parseInt(stringValue) : stringValue;
		return intValue;
	}
};

export const firstLetterUpperCase = (c?: string): string => {
	if (!c) return '';
	return c
		.toLowerCase()
		.replace(/[_ ]+/g, ' ') // Replace underscores or multiple spaces with a single space
		.split(' ')
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(' ');
};

export const displayPreworkStatus = (
	candidatePreWorkStatus: PreworkCompletionStatus,
	mentorPreWorkStatus: PreworkCompletionStatus
): string => {
	if (mentorPreWorkStatus === PreworkCompletionStatus.COMPLETED) {
		return 'approved';
	} else if (
		mentorPreWorkStatus === PreworkCompletionStatus.PARTIALLY_COMPLETED ||
		mentorPreWorkStatus === PreworkCompletionStatus.INCOMPLETE
	) {
		return 'allowMoreTime';
	} else if (candidatePreWorkStatus === PreworkCompletionStatus.COMPLETED) {
		return 'submitted';
	} else {
		return 'pending';
	}
};

export const displayPreworkRecommendationStatus = (
	recommendationStatus: PreworkReviewAction | undefined
): string => {
	if (recommendationStatus === PreworkReviewAction.PREWORK_APPROVE) {
		return 'approved';
	} else if (recommendationStatus === PreworkReviewAction.PREWORK_REJECT) {
		return 'allowMoreTime';
	} else if (
		recommendationStatus === PreworkReviewAction.EXTEND_TO_PREWORK_MENTOR
	) {
		return 'extendPrework';
	} else {
		return 'pending';
	}
};

export const displayTranslatedPreworkStatus = (
	preworkStatus: PreworkStatus | undefined
): string => {
	switch (preworkStatus) {
		case PreworkStatus.PREWORK_APPROVED:
			return 'approved';
		default:
			return 'pending';
	}
};

export const getAdminCentralTeamFilters = (
	categoryKey?: string | null,
	CTDFilter?: CentralTeamDashboardFilter
): ApplicationsFilter | undefined => {
	if (!categoryKey) return;

	const commonStatus = [ApplicationStatus.APPROVED, ApplicationStatus.REJECTED];

	switch (categoryKey) {
		case CTDCategories.ALL_APPROVED:
			return {
				status: commonStatus,
				zcReview: [ReviewAction.APPROVE, ReviewAction.APPROVE_IDK_CANDIDATE],
				proposerReview: [ReviewAction.APPROVE],
				approverReview: [ReviewAction.APPROVE],
				regionIds: CTDFilter?.regionId,
				zoneId: CTDFilter?.zoneId,
			};

		case CTDCategories.ALL_REJECTED:
			return {
				status: commonStatus,
				zcReview: [ReviewAction.REJECT],
				proposerReview: [ReviewAction.REJECT],
				approverReview: [ReviewAction.REJECT],
				regionIds: CTDFilter?.regionId,
				zoneId: CTDFilter?.zoneId,
			};

		case CTDCategories.ZC_REJECTED_PROPOSER_AND_RC_APPROVED:
			return {
				status: commonStatus,
				zcReview: [ReviewAction.REJECT],
				proposerReview: [ReviewAction.APPROVE],
				approverReview: [ReviewAction.APPROVE],
				regionIds: CTDFilter?.regionId,
				zoneId: CTDFilter?.zoneId,
			};

		case CTDCategories.RC_AND_ZC_REJECTED_PROPOSER_APPROVED:
			return {
				status: commonStatus,
				zcReview: [ReviewAction.REJECT],
				proposerReview: [ReviewAction.APPROVE],
				approverReview: [ReviewAction.REJECT],
				regionIds: CTDFilter?.regionId,
				zoneId: CTDFilter?.zoneId,
			};

		case CTDCategories.PROPOSER_AND_ZC_APPROVED_RC_REJECTED:
			return {
				status: commonStatus,
				zcReview: [ReviewAction.APPROVE, ReviewAction.APPROVE_IDK_CANDIDATE],
				proposerReview: [ReviewAction.APPROVE],
				approverReview: [ReviewAction.REJECT],
				regionIds: CTDFilter?.regionId,
				zoneId: CTDFilter?.zoneId,
			};

		case CTDCategories.PROPOSER_REJECTED_ZC_and_RC_APPROVED:
			return {
				status: commonStatus,
				zcReview: [ReviewAction.APPROVE, ReviewAction.APPROVE_IDK_CANDIDATE],
				proposerReview: [ReviewAction.REJECT],
				approverReview: [ReviewAction.APPROVE],
				regionIds: CTDFilter?.regionId,
				zoneId: CTDFilter?.zoneId,
			};

		case CTDCategories.PROPOSER_AND_RC_REJECTED_ZC_APPROVED:
			return {
				status: commonStatus,
				zcReview: [ReviewAction.APPROVE, ReviewAction.APPROVE_IDK_CANDIDATE],
				proposerReview: [ReviewAction.REJECT],
				approverReview: [ReviewAction.REJECT],
				regionIds: CTDFilter?.regionId,
				zoneId: CTDFilter?.zoneId,
			};

		case CTDCategories.PROPOSER_AND_ZC_REJECTED_RC_APPROVED:
			return {
				status: commonStatus,
				zcReview: [ReviewAction.REJECT],
				proposerReview: [ReviewAction.REJECT],
				approverReview: [ReviewAction.APPROVE],
				regionIds: CTDFilter?.regionId,
				zoneId: CTDFilter?.zoneId,
			};

		case CTDCategories.PROPOSER_APPROVED_AND_RC_REJECTED_BUT_ZC_NOT_PROVIDED_RECOMMENDATION_COUNT:
			return {
				status: commonStatus,
				proposerReview: [ReviewAction.APPROVE],
				approverReview: [ReviewAction.REJECT],
				zcReview: [ReviewAction.NOT_APPLICABLE],
				regionIds: CTDFilter?.regionId,
				zoneId: CTDFilter?.zoneId,
			};

		case CTDCategories.PROPOSER_REJECTED_AND_RC_APPROVED_BUT_ZC_NOT_PROVIDED_RECOMMENDATION_COUNT:
			return {
				status: commonStatus,
				proposerReview: [ReviewAction.REJECT],
				approverReview: [ReviewAction.APPROVE],
				zcReview: [ReviewAction.NOT_APPLICABLE],
				regionIds: CTDFilter?.regionId,
				zoneId: CTDFilter?.zoneId,
			};

		case CTDCategories.PROPOSER_AND_RC_APPROVED_BUT_ZC_NOT_PROVIDED_RECOMMENDATION_COUNT:
			return {
				status: commonStatus,
				proposerReview: [ReviewAction.APPROVE],
				approverReview: [ReviewAction.APPROVE],
				zcReview: [ReviewAction.NOT_APPLICABLE],
				regionIds: CTDFilter?.regionId,
				zoneId: CTDFilter?.zoneId,
			};

		case CTDCategories.PROPOSER_AND_RC_REJECTED_BUT_ZC_NOT_PROVIDED_RECOMMENDATION_COUNT:
			return {
				status: commonStatus,
				proposerReview: [ReviewAction.REJECT],
				approverReview: [ReviewAction.REJECT],
				zcReview: [ReviewAction.NOT_APPLICABLE],
				regionIds: CTDFilter?.regionId,
				zoneId: CTDFilter?.zoneId,
			};
	}
};

export const getFullName = (
	applicant: ApplicantInfo | Preceptor | undefined
): string => {
	return `${applicant?.firstName ?? ''} ${applicant?.middleName ?? ''} ${
		applicant?.lastName ?? ''
	}`.trim();
};

export const displayError = (error: Error | AxiosError | unknown) => {
	let errorMsg = 'Unknown error occurred';
	if (error instanceof AxiosError) {
		// eslint-disable-next-line
		errorMsg = error?.response?.data?.message || error.message;
	} else if (error instanceof Error) {
		errorMsg = error.message;
	}
	return errorMsg;
};

export const formatDateToISOString = (
	date: dayjs.Dayjs | Date | undefined | null,
	type: 'start' | 'end' = 'start'
): string | undefined => {
	if (!date) return undefined;
	const baseDate = dayjs(date).format('YYYY-MM-DD');
	const adjustedDate =
		type === 'start'
			? dayjs.utc(baseDate).startOf('day')
			: dayjs.utc(baseDate).endOf('day');
	return adjustedDate.toISOString();
};

export const checkIfDateIsSameOrBefore = (
	date: string | undefined
): boolean => {
	const momentDate = moment(date);

	return (
		momentDate.isValid() &&
		!momentDate.isSame(DEFAULT_DATE) &&
		momentDate.isSameOrBefore(moment())
	);
};

export const getDisableBulkAssign = (): boolean => {
	const stored = sessionStorage.getItem('DISABLE_BULK_ASSIGN');
	return stored ? (JSON.parse(stored) as boolean) : false;
};

export const hasPrivilege = (
	privilegeCode: string,
	userPrivileges: RolePrivilege[] | null | undefined
): boolean =>
	userPrivileges?.some((p) => p.privilege.code === privilegeCode) ?? false;

export const normalizeFunctionaryType = (
	value?: string
): string | undefined => {
	if (!value) return value;

	if (FUNCTIONARY_TYPE_LABELS[value]) {
		return FUNCTIONARY_TYPE_LABELS[value];
	}

	return value
		.toUpperCase()
		.replace(/^IS_/, '')
		.replace(/\s+/g, '_')
		.toLowerCase()
		.replace(/_/g, ' ')
		.replace(/\b\w/g, (c) => c.toUpperCase());
};

export const formatDate = (date: Date) => date.toISOString().split('T')[0];

export const rolesCanAccess = (role?: UserRole | null) => {
	return !!role && SELECTED_ROLES.includes(role);
};

export const getDisplayGender = (gender?: string) => {
	switch (gender?.toUpperCase()) {
		case 'M':
			return 'Male';

		case 'F':
			return 'Female';

		default:
			return '-';
	}
};

export const isApproverReviewEditable = (
	selectedRole?: UserRole | null,
	approverReviewEndDate?: string
) => {
	const isApprover = selectedRole === UserRole.APPROVER;

	if (!isApprover) {
		return false;
	}

	if (!approverReviewEndDate) {
		return false;
	}

	return new Date(approverReviewEndDate) > new Date();
};

export const isApplicationReadOnly = (
	selectedRole?: UserRole | null,
	approverReviewEndDate?: string
) => {
	const editableRoles = [UserRole.PROPOSER, UserRole.ZC, UserRole.CARE_TEAM];

	if (editableRoles.includes(selectedRole as UserRole)) {
		return false;
	}

	// APPROVER: read-only when outside the review window
	if (selectedRole === UserRole.APPROVER) {
		return !isApproverReviewEditable(selectedRole, approverReviewEndDate);
	}

	// All other roles can edit
	return true;
};

export const buildIndexes = (groups: GroupNode[]) => {
	const groupMap: GroupMap = {};
	const childrenMap: ChildrenMap = {};

	for (const node of groups) {
		groupMap[node.id] = node;

		const parentId = node.parent_id;

		if (parentId !== null && parentId !== undefined && parentId !== 0) {
			if (!childrenMap[parentId]) {
				childrenMap[parentId] = [];
			}
			childrenMap[parentId].push(node);
		}
	}

	return { groupMap, childrenMap };
};

export const isRoot = (parentId?: number | null) =>
	parentId === null || parentId === undefined || parentId === 0;

export const findRegionById = (id: number, groupMap: GroupMap) => {
	let current = groupMap[id];
	const visited = new Set<number>();

	while (current) {
		if (visited.has(current.id)) return null;
		visited.add(current.id);

		if (current.group_type === 'region') {
			return current;
		}

		const parentId = current.parent_id;

		if (isRoot(parentId) || parentId == null) {
			return null;
		}

		current = groupMap[parentId];
	}

	return null;
};

export const getZonesByRegionId = (
	regionId: number,
	groupMap: GroupMap,
	childrenMap: ChildrenMap
): GroupNode[] => {
	const region = groupMap[regionId];
	if (!region) return [];

	const result: GroupNode[] = [];
	const stack: GroupNode[] = [region];

	while (stack.length) {
		const node = stack.pop();
		if (!node) continue;
		const children = childrenMap[node.id] || [];

		for (const child of children) {
			if (child.group_type === 'zone') {
				result.push(child);
			}

			stack.push(child); // keep exploring regardless of type
		}
	}

	return result;
};

extend(customParseFormat);
export default dayjs;

export const formatFunctionaryType = (code: string): string => {
	if (!code) return '';

	return (
		FUNCTIONARY_TYPE_LABELS[code] ??
		code
			.toLowerCase()
			.split('_')
			.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
			.join(' ')
	);
};

export interface FetchDeduper {
	shouldSkip: (fetchKey: string) => boolean;
	markInflight: (fetchKey: string) => void;
	markCompleted: (fetchKey: string) => void;
	markFailed: () => void;
	reset: () => void;
}

export function createFetchDeduper(): FetchDeduper {
	let completedKey: string | null = null;
	let inflightKey: string | null = null;

	return {
		shouldSkip: (fetchKey) =>
			fetchKey === completedKey || fetchKey === inflightKey,
		markInflight: (fetchKey) => {
			inflightKey = fetchKey;
		},
		markCompleted: (fetchKey) => {
			completedKey = fetchKey;
			inflightKey = null;
		},
		markFailed: () => {
			inflightKey = null;
		},
		reset: () => {
			completedKey = null;
			inflightKey = null;
		},
	};
}

export function hasFilterValue(value: unknown): boolean {
	if (value === undefined || value === null) return false;
	if (typeof value === 'string') return value.trim() !== '';
	if (typeof value === 'number') return !Number.isNaN(value); // keeps 0; drops NaN
	return true;
}

export function removeEmptyFilters<T extends Record<string, unknown>>(
	filters: T
): Partial<T> {
	return Object.fromEntries(
		Object.entries(filters)
			.map(
				([key, value]) =>
					[key, typeof value === 'string' ? value.trim() : value] as const
			)
			.filter(([, value]) => hasFilterValue(value))
	) as Partial<T>;
}

export const FUNCTIONARY_ROLES = [
	UserRole.APPROVER,
	UserRole.CARE_TEAM,
	UserRole.ZC,
	UserRole.SNP_ADMIN,
];

export const ALLOWED_ROLES = [
	UserRole.PROPOSER,
	UserRole.CARE_TEAM,
	UserRole.ZC,
];
